/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps } from 'preact';

import { ListItemContext } from '#UNSAFE_Collection';
import { Menu } from '#UNSAFE_Menu';
import { ListViewContextMenuConfig } from './List.types';
import { isMenuChildrenFalsy } from '#UNSAFE_Menu/menuUtils';

export type Props<K extends string | number, D> = Omit<ComponentProps<typeof Menu>, 'children'> & {
  contextMenuConfig: ListViewContextMenuConfig<K, D>;
} & { contextMenuContext: ListItemContext<K, D> } & {
  testId: ComponentProps<typeof Menu>['testId'];
};

/**
 * The internal component used to render a custom context menu
 */
export function ListViewContextMenu<K extends string | number, D>({
  contextMenuConfig,
  testId,
  contextMenuContext,
  ...menuProps
}: Props<K, D>) {
  const accessibleLabel = contextMenuConfig.accessibleLabel;

  let menuItems: ComponentChildren;
  // Important to call items renderer only when isOpen is true so we know there is a contextMenuContext
  if (menuProps.isOpen) {
    menuItems = contextMenuConfig.itemsRenderer(contextMenuContext);
    if (isMenuChildrenFalsy(menuItems)) {
      return null;
    }
  } else {
    menuItems = null;
  }

  return (
    <Menu
      {...menuProps}
      {...(accessibleLabel && {
        'aria-label': accessibleLabel
      })}
      testId={testId}>
      {menuItems}
    </Menu>
  );
}
